import * as React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
// useReducedMotion is temporarily unused — see the commented-out check in PersonaSection.
import { motion, useInView /* , useReducedMotion */ } from "motion/react";
import { RiArrowRightSLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { Picture } from "~/components/ui/picture";
import { SectionTitle } from "~/components/ui/section-title";
import { cn } from "~/lib/utils";
import type { ProductPersonaContent, ProductPersonaItem } from "~/data/products/types";

interface PersonaSectionProps {
  content: ProductPersonaContent;
}

// Sizing is pure CSS (flex-basis / height), never JS-measured — the cards are the
// right size on the very first paint, before any JS has run.
const CONTAINER_MS = 500;
const CONTAINER_TRANSITION_DESKTOP = "transition-[flex] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]";
const CONTAINER_TRANSITION_MOBILE = "transition-[height] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]";
// Text/CTA fade in only once the container's own resize (CONTAINER_MS) has finished,
// so the copy never renders — and visibly reflows — inside a still-narrow card.
const CONTENT_FADE = { duration: 0.25, delay: CONTAINER_MS / 1000 };
// Card shadow glow shown only around the expanded card — matches Figma node
// 5061:3161's shadow (an orange-tinted glow, not a neutral drop shadow).
const EXPANDED_SHADOW = "shadow-[0px_12px_24px_0px_rgba(255,92,0,0.12)]";
// The 2px stroke is its own overlay, rendered after the card's content so it paints on
// top of the photo (an outline or border on the card itself ends up under the
// absolutely positioned image). Being absolute, it takes no layout space like Figma's
// stroke, so the desktop text column keeps Figma's full 231px at xl (a border ate 4px
// of it, enough to wrap "and vehicles in sync." onto a third line). It sits outside the
// card's clip, and the expanded card's content is clipped 1px in (EXPANDED_CLIP) so its
// anti-aliased edge lands under the opaque stroke — clipped to the same edge, the photo's
// GPU layer showed a dark fringe outside the stroke at the rounded corners.
const EXPANDED_CLIP = "[clip-path:inset(1px_round_15px)]";
function ExpandedFrame() {
  return <div aria-hidden className="border-bouncie-300 pointer-events-none absolute inset-0 z-10 rounded-2xl border-2" />;
}

// The carousel starts on the first persona and auto-advances every
// AUTO_ADVANCE_SECONDS, wrapping back to the first after the last. The very first
// advance comes sooner (FIRST_ADVANCE_MS) so visitors notice it's a carousel. Hovering the
// cards, or keyboard focus anywhere in the section, pauses it; any click inside the section
// stops it for good. The reduced-motion opt-out is temporarily commented out below, so
// visitors who prefer reduced motion still get auto-cycling.
const DEFAULT_EXPANDED_INDEX = 0;
// Card CTAs stay hidden until the persona landing pages exist.
const SHOW_PERSONA_CTA = false;
const AUTO_ADVANCE_SECONDS = 5;
const FIRST_ADVANCE_MS = 1500;

interface PersonaCardProps {
  item: ProductPersonaItem;
  isExpanded: boolean;
  onSelect: () => void;
  t: TFunction;
}

// object-position for the focal point, plus an optional zoom past object-cover that
// scales around that same point so it stays put.
function imageCropStyle(position = "50% 50%", zoom?: number): React.CSSProperties {
  return zoom
    ? { objectPosition: position, transform: `scale(${zoom})`, transformOrigin: position }
    : { objectPosition: position };
}

function DesktopPersonaCard({ item, isExpanded, onSelect, t }: PersonaCardProps) {
  return (
    <div
      className={cn(
        "relative h-full rounded-2xl",
        CONTAINER_TRANSITION_DESKTOP,
        isExpanded ? "flex-1" : "flex-[0_0_114px]",
        isExpanded && EXPANDED_SHADOW,
      )}
    >
      <div
        className={cn(
          // transform-gpu forces this rounded+clipped card onto its own GPU layer,
          // matching the layer the photo (absolute + object-cover, plus a transform for
          // some personas) already gets promoted to — without it, the rounded-corner
          // clip and the photo composite on mismatched layers and the curve rasterizes
          // jaggedly right where the photo meets the radius.
          "bg-bouncie-900 absolute inset-0 overflow-hidden rounded-2xl transform-gpu",
          isExpanded && EXPANDED_CLIP,
        )}
      >
        {isExpanded ? (
          <div className="flex h-full items-stretch">
            {/* Figma: 257px image in the 536px expanded card, which is the card's width at
                xl. Below xl the card is narrower, so the image keeps Figma's ratio
                (257/536 ≈ 47.9%) instead of squeezing the text column. */}
            <div className="relative h-full w-[47.9%] shrink-0 overflow-hidden xl:w-[257px]">
              <Picture
                src={item.image}
                alt={t(item.imageAlt)}
                className={cn("absolute inset-0 size-full object-cover", item.imageFlipDesktop && "-scale-x-100")}
                style={imageCropStyle(item.imagePositionDesktop, item.imageZoomDesktop)}
              />
              <div className="absolute inset-0 bg-neutral-500/15" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col justify-between px-6 py-10">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={CONTENT_FADE}
                className="flex flex-col gap-3"
              >
                <p className="text-figma-blue-arctic text-figma-body-sm-size leading-5 uppercase">{t(item.eyebrow)}</p>
                <p className="font-display text-figma-titles-sm-size text-figma-text-headers-secondary leading-[1.15] font-extrabold whitespace-pre-line">{t(item.title)}</p>
                <p className="text-figma-body-xs-size text-figma-grey-mist leading-[18px]">{t(item.description)}</p>
              </motion.div>
              {SHOW_PERSONA_CTA && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={CONTENT_FADE}
                  className="flex justify-end"
                >
                  <Button data-sid={`pdp-persona-cta:${item.eyebrow}`}
                    variant="link"
                    size="sm"
                    // size="sm" carries its own px-6 for hit-area padding, which stacked with
                    // the card's own px-6 pushed the icon 48px from the edge instead of the
                    // 24px Figma specifies — px-0 leaves only the card's padding.
                    className="text-figma-body-md-size text-figma-text-cta-default gap-1 px-0"
                    iconRight={<RiArrowRightSLine className="size-5" />}
                    // No dedicated per-persona landing page exists yet to link to.
                    onClick={() => alert("TODO: Link to a persona-specific landing page once one exists")}
                  >
                    {t(item.ctaLabel)}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        ) : (
          <button data-sid={`pdp-persona-desktop-select:${item.eyebrow}`}
            type="button"
            onClick={onSelect}
            className="focus-visible:ring-ring relative flex size-full items-end justify-end overflow-hidden px-6 py-6 focus-visible:ring-1 focus-visible:outline-none"
          >
            <Picture
              src={item.image}
              alt=""
              className={cn("absolute inset-0 size-full object-cover", item.imageFlipDesktop && "-scale-x-100")}
              style={imageCropStyle(
                item.imagePositionDesktopCollapsed ?? item.imagePositionDesktop,
                item.imageZoomDesktopCollapsed,
              )}
            />
            <div className="bg-bouncie-900/75 absolute inset-0" />
            {/* writing-mode (not a rotate transform) so the box's own layout dimensions
                already match its rotated footprint — a transform-rotate here would keep
                the pre-rotation (wide, short) box for flex's items-end/justify-end math,
                clipping most of the label for anything longer than a couple words.
                rotate-180 flips vertical-rl's default top-to-bottom read direction to
                bottom-to-top, matching Figma's -rotate-90. leading-[1.15]
                matters more here than usual: in vertical writing-mode, line-height
                becomes the text block's *width*, so an unset (larger default) line-height
                was pushing the label further from the card edge than the real 24px
                padding. */}
            <span className="font-display text-figma-titles-sm-size text-figma-text-headers-secondary relative rotate-180 leading-[1.15] font-bold tracking-[0.24px] whitespace-nowrap uppercase [writing-mode:vertical-rl]">
              {t(item.eyebrow)}
            </span>
          </button>
        )}
      </div>
      {isExpanded && <ExpandedFrame />}
    </div>
  );
}

function MobilePersonaCard({ item, isExpanded, onSelect, t }: PersonaCardProps) {
  return (
    <div
      className={cn(
        "relative w-full rounded-2xl",
        CONTAINER_TRANSITION_MOBILE,
        isExpanded ? "h-[324px]" : "h-[65px]",
        isExpanded && EXPANDED_SHADOW,
      )}
    >
      <div
        className={cn(
          // See DesktopPersonaCard — transform-gpu keeps the rounded-corner clip and the
          // photo compositing on the same GPU layer so the curve doesn't rasterize jaggedly.
          "bg-bouncie-900 absolute inset-0 overflow-hidden rounded-2xl transform-gpu",
          isExpanded && EXPANDED_CLIP,
        )}
      >
        {isExpanded ? (
          <div className="flex h-full flex-col">
            <div className="relative h-[159px] w-full shrink-0">
              <Picture
                src={item.image}
                alt={t(item.imageAlt)}
                className="absolute inset-0 size-full object-cover"
                style={{ objectPosition: item.imagePositionMobile ?? "50% 50%" }}
              />
              <div className="absolute inset-0 bg-neutral-500/20" />
            </div>
            <div className="flex flex-1 flex-col justify-between px-6 py-6">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={CONTENT_FADE}
                className="flex flex-col gap-2"
              >
                <p className="font-display text-figma-body-l-size text-figma-text-headers-secondary leading-[1.15] font-bold tracking-[0.18px] uppercase">
                  {t(item.eyebrow)}
                </p>
                <p className="text-figma-body-xs-size text-figma-grey-mist leading-[18px]">{t(item.description)}</p>
              </motion.div>
              {SHOW_PERSONA_CTA && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={CONTENT_FADE}
                  className="flex justify-end"
                >
                  <Button data-sid={`pdp-persona-select-cta:${item.eyebrow}`}
                    variant="link"
                    size="sm"
                    // size="sm" carries its own px-6 for hit-area padding, which stacked with
                    // the card's own px-6 pushed the icon 48px from the edge instead of the
                    // 24px Figma specifies — px-0 leaves only the card's padding.
                    className="text-figma-body-md-size text-figma-text-cta-default gap-1 px-0"
                    iconRight={<RiArrowRightSLine className="size-5" />}
                    // No dedicated per-persona landing page exists yet to link to.
                    onClick={() => alert("TODO: Link to a persona-specific landing page once one exists")}
                  >
                    {t(item.ctaLabel)}
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        ) : (
          <button data-sid={`pdp-persona-mobile-select:${item.eyebrow}`}
            type="button"
            onClick={onSelect}
            className="focus-visible:ring-ring relative flex size-full items-end overflow-hidden px-6 pt-6 pb-4 text-left focus-visible:ring-1 focus-visible:outline-none"
          >
            <Picture
              src={item.image}
              alt=""
              className="absolute inset-0 size-full object-cover"
              style={{ objectPosition: item.imagePositionMobileCollapsed ?? item.imagePositionMobile ?? "50% 50%" }}
            />
            <div className="bg-bouncie-900/75 absolute inset-0" />
            {/* text-md, not text-lg — same theme-scale mismatch as the expanded mobile
                eyebrow above; Figma's spec here (18px) is this project's text-md. */}
            <span className="font-display text-figma-body-l-size text-figma-text-headers-secondary relative leading-[1.15] font-bold tracking-[0.18px] uppercase">
              {t(item.eyebrow)}
            </span>
          </button>
        )}
      </div>
      {isExpanded && <ExpandedFrame />}
    </div>
  );
}

export function PersonaSection({ content }: PersonaSectionProps) {
  const { t } = useTranslation("products");
  const [expandedIndex, setExpandedIndex] = React.useState(DEFAULT_EXPANDED_INDEX);
  const [isHovered, setIsHovered] = React.useState(false);
  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);
  const [userHasInteracted, setUserHasInteracted] = React.useState(false);
  const sectionRef = React.useRef<HTMLElement>(null);
  // Don't start cycling until the section is actually on screen, so visitors who
  // scroll down still land on the first persona.
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
  // Temporarily disabled: reduced-motion preference no longer stops auto-cycling.
  // const prefersReducedMotion = useReducedMotion();
  // Only the first auto-advance uses the short FIRST_ADVANCE_MS delay.
  const hasAutoAdvancedRef = React.useRef(false);

  // Re-created whenever hover or focus ends, so the current persona gets a full dwell
  // after the pause lifts rather than advancing immediately.
  React.useEffect(() => {
    if (!isInView || isHovered || hasFocusWithin || userHasInteracted /* || prefersReducedMotion */) return;
    let timeout: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      const delay = hasAutoAdvancedRef.current ? AUTO_ADVANCE_SECONDS * 1000 : FIRST_ADVANCE_MS;
      timeout = setTimeout(() => {
        hasAutoAdvancedRef.current = true;
        setExpandedIndex((current) => (current + 1) % content.items.length);
        scheduleNext();
      }, delay);
    };
    scheduleNext();
    return () => clearTimeout(timeout);
  }, [isInView, isHovered, hasFocusWithin, userHasInteracted, /* prefersReducedMotion, */ content.items.length]);

  return (
    <section
      ref={sectionRef}
      className="py-figma-padding-padding-t-b-pt-b-40 bg-white"
      // React's focus events bubble, so these track focus anywhere inside the section.
      // Moving focus between pills/cards fires blur then focus; only a blur whose new
      // target is outside the section resumes cycling.
      onFocus={() => setHasFocusWithin(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setHasFocusWithin(false);
      }}
      // Capture phase so every click in the section (pills, cards, CTAs) stops the
      // cycle before the target's own handler runs.
      onClickCapture={() => setUserHasInteracted(true)}
    >
      {/* Mobile: Figma puts 24px between the header and the stacked cards (pills are hidden). */}
      <div className="wrap flex flex-col items-center gap-6 md:gap-10">
        {/* Mobile (below md) follows Figma's mobile Tagline: centered, 8px gap. */}
        <div className="flex w-full flex-col items-center gap-2 text-center md:items-start md:gap-4 md:text-left">
          <p className="eyebrow">
            {t(content.eyebrow)}
          </p>
          <SectionTitle className="text-figma-text-headers-primary">
            {t(content.headline)}
          </SectionTitle>
        </div>
        {/* Pills are hidden on mobile — the stacked cards below are already tappable. */}
        <div className="flex flex-wrap items-center justify-center gap-3 max-md:hidden">
          {content.items.map((item, index) => {
            const isSelected = index === expandedIndex;
            return (
              <button
                key={item.eyebrow}
                type="button"
                onClick={() => setExpandedIndex(index)}
                aria-pressed={isSelected}
                // Figma draws the 1px stroke inside the 84×40 chip without adding to
                // its 20px/10px padding — border-box CSS would add it on top, so the
                // padding is trimmed by 1px to keep the chip at Figma's 40px height.
                className={cn(
                  "focus-visible:ring-ring rounded-full border px-[19px] py-[9px] text-sm font-bold tracking-[0.14px] transition-colors focus-visible:ring-1 focus-visible:outline-none",
                  isSelected
                    ? "border-bouncie-900 bg-bouncie-900 text-white"
                    : "border-neutral-100 bg-white text-text-body-secondary hover:border-bouncie-300",
                )}
              >
                {t(item.eyebrow)}
              </button>
            );
          })}
        </div>
        {/* Figma: collapsed cards 114×480, expanded 536×480. Five 114s + 536 + five 16px
            gaps is 1186px, 2px over xl's 1184px wrap, so the gaps shrink to 15.6px there
            and the flex-1 expanded card lands on exactly 536px. Below xl it just fills
            the remaining width. */}
        {/* Hover pauses only over the cards, not the header or pills. */}
        <div
          className="hidden h-[480px] w-full gap-4 lg:flex xl:gap-[15.6px]"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {content.items.map((item, index) => (
            <DesktopPersonaCard
              key={item.eyebrow}
              item={item}
              isExpanded={index === expandedIndex}
              onSelect={() => setExpandedIndex(index)}
              t={t}
            />
          ))}
        </div>
        <div
          className="flex w-full flex-col gap-2 lg:hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {content.items.map((item, index) => (
            <MobilePersonaCard
              key={item.eyebrow}
              item={item}
              isExpanded={index === expandedIndex}
              onSelect={() => setExpandedIndex(index)}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
